import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  inject,
  Input,
  PLATFORM_ID,
  signal,
  Signal,
} from '@angular/core';

import { TravelGuideDestinationsComponent } from '../../shared/components/sections/trending-domestic-destinations/trending-domestic-destinations.component';
import { DestinationsByMonthComponent } from '../../shared/components/sections/destinations-by-month/destinations-by-month.component';
import { BlogPostComponent } from '../../shared/components/sections/blog-post/blog-post.component';
import { TravellerStoriesComponent } from '../../shared/components/sections/traveller-stories/traveller-stories.component';
import { ScrollToTopButtonComponent } from '../../shared/components/scroll-to-top/scroll-to-top.component';
import { MetaTagsService } from '../../reducers/services/meta-tags.service';
import { HolidayPackagesComponent } from '../../shared/components/sections/holiday-destinations/holiday-packages.component';
import { ExpandedThemesComponent } from '../../shared/components/sections/expanded-themes/expanded-themes.component';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  selectTravellerStories,
} from './redux/selectors/home.selectors';
import { map, startWith, take } from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CanonicalService } from '../../utils/canonical.service';
import { LeadFormXlV2Component } from '../lead-form/lead-form-xl-v2/lead-form-xl-v2.component';
import { VideoCarouselComponent } from '../../shared/components/carousels/video-carousel/video-carousel.component';
import { SmallHeaderComponent } from '../../shared/partials/small-header/small-header.component';
import { HomeAboutComponent } from './home-about/home-about.component';
import {
  HomeOurPropertiesComponent,
} from './home-our-properties/home-our-properties.component';
import { FaqComponent } from '../faq/faq.component';
import { ServicesSectionComponent } from '../services-section/services-section.component';
import { SectionTitleComponent } from '../../shared/components/ui-elements/section-title/section-title.component';
import { DestinationsService } from '../../reducers/services/destinations.service';
import { UpcomingPropertyCardComponent } from '../../shared/components/cards/upcoming-property-card/upcoming-property-card.component';
import { HotelDiariesComponent } from '../../shared/components/sections/hotel-diaries/hotel-diaries.component';
import { HomeService } from './home.service';
import { PopupBannerComponent } from '../popup-banner/popup-banner.component';
import { HomeUpcomingPropertiesComponent } from './home-upcoming-properties/home-upcoming-properties.component';
import { CoverHeroBannerComponent } from '../../shared/components/banners/cover-hero-banner/cover-hero-banner.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    ScrollToTopButtonComponent,
    RouterModule,
    VideoCarouselComponent,
    CommonModule,
    SmallHeaderComponent,
    HomeAboutComponent,
    HomeOurPropertiesComponent,
    FaqComponent,
    ServicesSectionComponent,
    SectionTitleComponent,
    HotelDiariesComponent,
    PopupBannerComponent,
    HomeUpcomingPropertiesComponent,
    UpcomingPropertyCardComponent,
    CoverHeroBannerComponent
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeComponent {
  @Input() title: string = '';
  private metaTagsService = inject(MetaTagsService);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private canonicalService = inject(CanonicalService);
  travellerStoriesState: Signal<{ travellerStories: any[] }> = signal({
    travellerStories: [],
  });
  dataFAQ: any = [];
  activeKey: string | null = null;

  toggle(key: string) {
    this.activeKey = (this.activeKey === key) ? null : key;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private route: ActivatedRoute,
    private router: Router,
    private destinationsService: DestinationsService,
    private homeService: HomeService
  ) {
    this.route.queryParams.subscribe((params) => {
      const showLead = params['showLead'];
      if (showLead === 'true') {
        this.openLeadModal();
      }
    });

    this.setMetaTags();
    this.canonicalService.setCanonicalLinks('/', 1, 1);

    this.travellerStoriesState = toSignal(
      this.store.select(selectTravellerStories).pipe(
        take(1),
        startWith({ travellerStories: [], isLoading: false, error: null }),
        map((state: any) =>
          state
            ? { travellerStories: state.travellerStories?.data || [] }
            : { travellerStories: [] }
        )
      ),
      { initialValue: { travellerStories: [] } }
    );
  }

  ngOnInit() {
    this.loadFAQ();
  }

  ngAfterViewInit() {
    if (!window.matchMedia('(min-width: 769px)').matches) return;

    const descriptions: any = {
      goodstay: 'Embrace a timeless elegance with refined excellence.',
      goodfood: 'Indulge in unforgettable taste and culinary hospitality.',
      goodpeople: 'Experience genuine service and heartfelt interactions.'
    };

    const icons: any = {
      goodstay: 'assets/images/icons/bed.svg',
      goodfood: 'assets/images/icons/food-tray.svg',
      goodpeople: 'assets/images/icons/people.svg'
    };

    const container = document.querySelector('.values-container') as HTMLElement;
    const overlay = document.querySelector('.values-hover-overlay') as HTMLElement;
    const title = document.getElementById('hover-title')!;
    const desc = document.getElementById('hover-desc')!;
    const logo = document.getElementById('hover-logo') as HTMLImageElement;

    const videos: any = {
      goodstay: document.getElementById('video-goodstay'),
      goodfood: document.getElementById('video-goodfood'),
      goodpeople: document.getElementById('video-goodpeople')
    };

    Object.values(videos).forEach((v: any) => (v.style.display = 'none'));

    container.addEventListener('mouseover', () => {
      const key = this.activeKey;
      if (!key) return;

      title.innerText = '#' + key;
      desc.innerText = descriptions[key] || '';
      logo.src = icons[key] || '';

      Object.entries(videos).forEach(([k, v]: any) => {
        v.style.display = k === key ? 'block' : 'none';
      });

      overlay.classList.add('active');
    });

    container.addEventListener('mouseleave', () => {
      overlay.classList.remove('active');
    });
  }

  redirectToTheme(theme: any) {
    const slugPath = theme.toLowerCase();
    this.router.navigate(['/', slugPath], {
      state: {
        theme: theme?._id,
        sortBy: 'price',
        sortOrder: 'asc',
        page: 1,
        limit: 15,
      },
    });
  }
  openLeadModal() {
    this.dialog.open(LeadFormXlV2Component, {
      height: 'auto',
      width: '700px',
      hasBackdrop: true,
      backdropClass: 'custom-backdrop',
      panelClass: ['lead-form-panel', 'lead-form-border-radius'],
      disableClose: true,
      autoFocus: false,
    });
  }

  private setMetaTags(): void {
    const url = isPlatformBrowser(this.platformId) ? window.location.href : '';
    this.metaTagsService.setMetaTags(
      'Udaan Hotels & Resorts ~ Best Pure Veg Hotels in Darjeeling & Gangtok',
      url,
      'A Luxury stay experience amidst the timeless tranquil. Our array of pure veg hotels and resorts is located in Darjeeling, Gangtok, Lachung, Pelling, Siliguri &amp; Kalimpong. udaanhotels.com',
      ''
    );
  }
  loadFAQ = () => {
    const payload = {
      page: 1,
      limit: 5,
    };

    this.homeService?.getFaq(payload).subscribe({
      next: (response: any) => {
        this.dataFAQ = response?.data || [];
      },
      error: (error: any) => {
        console.error('Error fetching FAQs:', error);
      },
    });
  };

  viewAllFAQ(): void {
    this.router.navigate(['/faqs']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  private isTouchDevice(): boolean {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  onHover(key: string | null) {
    if (this.isTouchDevice()) return;
    this.activeKey = key;
  }

  onTouch(event: TouchEvent, key: string) {
    if (!this.isTouchDevice()) return;

    // event.preventDefault();
    this.openAndPlay(event.currentTarget as HTMLElement, key);
  }

  onClick(event: MouseEvent, key: string) {
    if (this.isTouchDevice()) {
      return;
    }

    this.activeKey = (this.activeKey === key) ? null : key;
  }

  private openAndPlay(cardEl: HTMLElement, key: string) {
    const wasActive = this.activeKey === key;
    this.activeKey = wasActive ? null : key;

    const wrapper = document.querySelector('.values-wrapper') as HTMLElement | null;
    if (wrapper) {
      const allVideos = Array.from(wrapper.querySelectorAll('video')) as HTMLVideoElement[];
      allVideos.forEach(v => {
        try { v.pause(); } catch { }
      });
    }

    if (wasActive) return;

    const video = cardEl.querySelector('video') as HTMLVideoElement | null;
    if (!video) return;

    video.muted = true;
    video.playsInline = true;

    const p = video.play();
    if (p && typeof (p as any).catch === 'function') {
      (p as Promise<void>).catch(() => { });
    }
  }
}
